'use client';

import { useCallback, useMemo, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { CopyIcon, PlusIcon, XIcon } from 'lucide-react';

import { useCustomerCtx } from '@/app/(app)/customers/[id]/provider';
import { EmployeeAvatar } from '@/components/molecules/employee-avatar';
import { SuperUserOnly } from '@/components/providers/admin-only';
import { ApiSchemas } from '@/components/providers/v1-api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useEditableState } from '@/hooks/use-editable-state';
import { copyToClipboard } from '@/lib/clipboard';
import { formatUSPhoneNumber } from '@/lib/phone';

export function CustomerOverview() {
  return (
    <div className='flex w-full flex-col gap-8'>
      <CustomerContactsSection />
      <CustomerLocationSection />
      <CustomerBillingLocationSection />
      <CustomerPinnedNotesSection />
    </div>
  );
}

function CustomerContactsSection() {
  const { customer } = useCustomerCtx();
  const { value } = useEditableState(customer);

  const onChange = useCallback((update: Partial<typeof value>) => {
    console.log(update);
  }, []);

  return (
    <div className='flex w-full flex-col gap-4'>
      <h2 className='text-heading-sm'>Contact</h2>
      <div className='grid grid-cols-2 gap-2'>
        <div className='space-y-2'>
          <Label htmlFor='first_name'>First Name</Label>
          <Input
            id='first_name'
            disabled
            value={value.first_name || ''}
            onChange={(e) => onChange({ first_name: e.target.value })}
          />
        </div>
        <div className='space-y-2'>
          <Label htmlFor='last_name'>Last Name</Label>
          <Input
            id='last_name'
            disabled
            value={value.last_name || ''}
            onChange={(e) => onChange({ last_name: e.target.value })}
          />
        </div>
        <div className='space-y-2'>
          <Label htmlFor='title'>Title</Label>
          <Input id='title' disabled value={value.title || ''} onChange={(e) => onChange({ title: e.target.value })} />
        </div>
        <div className='space-y-2'>
          <Label htmlFor='email'>Company</Label>
          <Input
            id='company'
            disabled
            value={value.company || ''}
            onChange={(e) => onChange({ company: e.target.value })}
          />
        </div>
        <CustomerEmails />
        <CustomerPhones />
      </div>
    </div>
  );
}

function CustomerEmails() {
  const { customer } = useCustomerCtx();
  const emails = useMemo(() => {
    const primaryEmails = customer.contacts.filter((contact) => contact.type === 'EMAIL' && contact.primary);
    const nonPrimaryEmails = customer.contacts.filter((contact) => contact.type === 'EMAIL' && !contact.primary);
    return [...primaryEmails, ...nonPrimaryEmails];
  }, [customer]);
  const [firstEmail, ...restEmails] = emails;

  return (
    <div className='col-span-2 flex flex-col gap-2'>
      <Label htmlFor='email'>Email</Label>
      <div className='flex items-center gap-2'>
        <div className='relative w-full'>
          <Input id='email' disabled value={firstEmail?.value || ''} onChange={(e) => console.log(e.target.value)} />
          <InputCopyButton text={firstEmail?.value || ''} />
        </div>
        <Button variant='outline' size='icon' disabled>
          <PlusIcon />
        </Button>
      </div>
      {restEmails.length > 0 && (
        <div className='mt-0.5 flex flex-col gap-2.5'>
          {restEmails.map((email, i) => (
            <div className='flex items-center gap-2' key={i}>
              <div className='relative w-full'>
                <Input disabled value={email.value || ''} onChange={(e) => console.log(e.target.value)} />
                <InputCopyButton text={email.value || ''} />
              </div>
              <Button variant='outline' size='icon' disabled>
                <XIcon />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CustomerPhones() {
  const { customer } = useCustomerCtx();
  const phones = useMemo(() => {
    const primaryPhones = customer.contacts.filter((contact) => contact.type === 'PHONE' && contact.primary);
    const nonPrimaryPhones = customer.contacts.filter((contact) => contact.type === 'PHONE' && !contact.primary);
    return [...primaryPhones, ...nonPrimaryPhones];
  }, [customer]);
  const [firstPhone, ...restPhones] = phones;

  return (
    <div className='col-span-2 flex flex-col gap-2'>
      <Label htmlFor='phone'>Phone</Label>
      <div className='flex items-center gap-2'>
        <div className='relative w-full'>
          <PhoneInput value={firstPhone?.value || ''} onChange={(e) => console.log(e)} />
          <InputCopyButton text={firstPhone?.value || ''} />
        </div>
        <Button variant='outline' size='icon' disabled>
          <PlusIcon />
        </Button>
      </div>
      {restPhones.length > 0 && (
        <div className='mt-0.5 flex flex-col gap-2.5'>
          {restPhones.map((phone, i) => (
            <div className='flex items-center gap-2' key={i}>
              <div className='relative w-full'>
                <PhoneInput value={phone.value || ''} onChange={(e) => console.log(e)} />
                <InputCopyButton text={phone.value || ''} />
              </div>
              <Button variant='outline' size='icon' disabled>
                <XIcon />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PhoneInput({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const [displayValue, setDisplayValue] = useState(formatUSPhoneNumber(value || ''));
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDisplayValue(e.target.value);
    onChange(e.target.value);
  };
  return <Input value={displayValue} onChange={handleChange} disabled />;
}

function InputCopyButton({ text }: { text: string }) {
  return (
    <button
      className='absolute top-1/2 right-0.5 -translate-y-1/2 cursor-pointer rounded-md p-1.5 transition-all duration-300 hover:bg-muted active:scale-95'
      onClick={() => copyToClipboard({ text, successMessage: 'Copied to clipboard' })}
    >
      <CopyIcon className='size-4' />
    </button>
  );
}

function CustomerLocationSection() {
  const { customer } = useCustomerCtx();
  const location = customer.primary_location;

  if (!location) {
    return (
      <div className='flex w-full flex-col gap-4'>
        <h2 className='text-heading-sm'>Location</h2>
        <div className='flex flex-row items-center gap-3 border-t-[0.5px] py-3 text-sm font-medium text-muted-foreground'>
          Location not set
        </div>
      </div>
    );
  }

  return (
    <div className='flex w-full flex-col gap-4'>
      <h2 className='text-heading-sm'>Location</h2>
      <div className='flex flex-row items-center gap-3 border-t-[0.5px] py-3'>
        <div className='space-y-1'>
          <div className='text-sm font-medium'>
            {location.line1} {location.line2}
          </div>
          <div className='text-xs text-muted-foreground'>
            {location.city}, {location.state} {location.zip}
          </div>
        </div>
      </div>
      <SuperUserOnly disclose>
        <div className='grid grid-cols-2 gap-x-3'>
          <div className='space-y-2'>
            <Label>Latitude</Label>
            <Input disabled value={location.latitude ?? ''} onChange={(e) => console.log(e.target.value)} />
          </div>
          <div className='space-y-2'>
            <Label>Longitude</Label>
            <Input disabled value={location.longitude ?? ''} onChange={(e) => console.log(e.target.value)} />
          </div>
        </div>
      </SuperUserOnly>
    </div>
  );
}

function CustomerBillingLocationSection() {
  const { customer } = useCustomerCtx();
  const location = useMemo(() => customer.locations.find((location) => location.billing), [customer]);
  const billingPhone = useMemo(
    () => customer.contacts.find((contact) => contact.type === 'PHONE' && contact.billing),
    [customer]
  );
  const billingEmail = useMemo(
    () => customer.contacts.find((contact) => contact.type === 'EMAIL' && contact.billing),
    [customer]
  );
  const onChange = useCallback((updates: Partial<typeof location>) => {
    console.log(updates);
  }, []);

  if (!location) {
    return null;
  }

  return (
    <div className='flex w-full flex-col gap-4'>
      <h2 className='text-heading-sm'>Billing Location</h2>
      <div className='grid grid-cols-2 gap-x-2 gap-y-3'>
        <div className='space-y-2'>
          <Label htmlFor='line1'>Line 1</Label>
          <Input id='line1' disabled value={location.line1} onChange={(e) => onChange({ line1: e.target.value })} />
        </div>
        <div className='space-y-2'>
          <Label htmlFor='line2'>Line 2</Label>
          <Input
            id='line2'
            disabled
            value={location.line2 || ''}
            onChange={(e) => onChange({ line2: e.target.value })}
          />
        </div>
        <div className='space-y-2'>
          <Label htmlFor='city'>City</Label>
          <Input id='city' disabled value={location.city} onChange={(e) => onChange({ city: e.target.value })} />
        </div>
        <div className='grid grid-cols-2 gap-x-2'>
          <div className='space-y-2'>
            <Label htmlFor='state'>State</Label>
            <Input id='state' disabled value={location.state} onChange={(e) => onChange({ state: e.target.value })} />
          </div>
          <div className='space-y-2'>
            <Label htmlFor='zip'>Zip</Label>
            <Input id='zip' disabled value={location.zip} onChange={(e) => onChange({ zip: e.target.value })} />
          </div>
        </div>
        <div className='space-y-2'>
          <Label htmlFor='email'>Email</Label>
          <Input id='email' disabled value={billingEmail?.value || ''} onChange={(e) => console.log(e.target.value)} />
        </div>
        <div className='space-y-2'>
          <Label htmlFor='phone'>Phone</Label>
          <PhoneInput value={billingPhone?.value || ''} onChange={(e) => console.log(e)} />
        </div>
      </div>
    </div>
  );
}

function CustomerPinnedNotesSection() {
  const { customer } = useCustomerCtx();
  const notes = useMemo(() => customer.notes.filter((note) => Boolean(note.pinned_at)), [customer]);

  if (notes.length === 0) {
    return null;
  }

  return (
    <div className='flex w-full flex-col gap-4'>
      <h2 className='text-heading-sm'>Pinned Notes</h2>
      <div className='flex flex-col gap-3 border-t-[0.5px]'>
        {notes.map((note) => (
          <CustomerNote key={note.id} note={note} />
        ))}
      </div>
    </div>
  );
}

function CustomerNote({ note }: { note: ApiSchemas['NoteRead'] }) {
  return (
    <div className='flex w-full items-start gap-4 py-3'>
      <EmployeeAvatar employeeId={note.created_by_id} />
      <div className='flex flex-col gap-1'>
        <div className='line-clamp-2 text-sm'>{note.content}</div>
        <div className='text-xs/none text-muted-foreground'>
          {formatDistanceToNow(note.created_at, { addSuffix: true })}
        </div>
      </div>
    </div>
  );
}
